// SPC convective outlook — the user's LOCAL severe risk. Fetches the SPC Day-1
// categorical GeoJSON (keyless, verified browser-CORS `access-control-allow-origin: *`),
// point-in-polygons the user's lat/lon, and takes the HIGHEST category containing them.
// Falls back Day-1 → Day-2 → Day-3 when the current outlook has expired. Pure + RN-portable.

export type SpcLabel = 'NONE' | 'TSTM' | 'MRGL' | 'SLGT' | 'ENH' | 'MDT' | 'HIGH'

export interface SpcMeta {
  num: number
  color: string
  word: string
}
const META: Record<SpcLabel, SpcMeta> = {
  NONE: { num: 0, color: '#6b7280', word: 'No severe' },
  TSTM: { num: 0, color: '#6b7280', word: 'No severe' },
  MRGL: { num: 1, color: '#63991a', word: 'Marginal' },
  SLGT: { num: 2, color: '#e6c930', word: 'Slight' },
  ENH: { num: 3, color: '#ef9f27', word: 'Enhanced' },
  MDT: { num: 4, color: '#e24b4a', word: 'Moderate' },
  HIGH: { num: 5, color: '#ed93b1', word: 'High' },
}
export const spcMeta = (label: SpcLabel): SpcMeta => META[label] ?? META.NONE

// ── point-in-polygon (ray casting, lon/lat) ─────────────────────────────────
type Ring = number[][] // [[lon,lat], ...]
function inRing(lon: number, lat: number, ring: Ring): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0]
    const yi = ring[i][1]
    const xj = ring[j][0]
    const yj = ring[j][1]
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}
function inPolygon(lon: number, lat: number, poly: Ring[]): boolean {
  if (!poly.length || !inRing(lon, lat, poly[0])) return false
  for (let i = 1; i < poly.length; i++) if (inRing(lon, lat, poly[i])) return false // in a hole
  return true
}
interface Geom {
  type: string
  coordinates: unknown
}
function inGeometry(lon: number, lat: number, g: Geom): boolean {
  if (g.type === 'Polygon') return inPolygon(lon, lat, g.coordinates as Ring[])
  if (g.type === 'MultiPolygon') return (g.coordinates as Ring[][]).some((p) => inPolygon(lon, lat, p))
  return false
}

interface SpcFeature {
  geometry: Geom
  properties: { DN?: number; LABEL?: string; LABEL2?: string; EXPIRE_ISO?: string }
}
interface SpcFC {
  features?: SpcFeature[]
}

export interface SpcRisk {
  label: SpcLabel
  /** Human descriptor from the feature (LABEL2), if any. */
  descriptor: string | null
  /** Which outlook day produced this (1–3). */
  day: number
}

async function fetchDay(day: number): Promise<SpcFC | null> {
  try {
    const res = await fetch(`https://www.spc.noaa.gov/products/outlook/day${day}otlk_cat.nolyr.geojson`, {
      headers: { Accept: 'application/geo+json' },
    })
    if (!res.ok) return null
    return (await res.json()) as SpcFC
  } catch {
    return null
  }
}

function expired(fc: SpcFC): boolean {
  const iso = fc.features?.[0]?.properties?.EXPIRE_ISO
  if (!iso) return false
  const t = Date.parse(iso)
  return Number.isFinite(t) && t < Date.now()
}

export async function fetchSpcRisk(lat: number, lon: number): Promise<SpcRisk> {
  for (const day of [1, 2, 3]) {
    const fc = await fetchDay(day)
    if (!fc?.features?.length) continue
    if (expired(fc)) continue // stale outlook → try the next day

    let best: { dn: number; label: SpcLabel; desc: string | null } | null = null
    for (const f of fc.features) {
      const label = (f.properties?.LABEL ?? 'NONE') as SpcLabel
      if (label === 'TSTM' || label === 'NONE') continue // general storms ≠ severe
      if (!META[label]) continue
      if (inGeometry(lon, lat, f.geometry)) {
        const dn = f.properties?.DN ?? META[label].num
        if (!best || dn > best.dn) best = { dn, label, desc: f.properties?.LABEL2 ?? null }
      }
    }
    // A non-expired outlook with polygons but no containing severe risk → genuinely NONE.
    return best ? { label: best.label, descriptor: best.desc, day } : { label: 'NONE', descriptor: null, day }
  }
  return { label: 'NONE', descriptor: null, day: 1 }
}
